import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { BiShow, BiHide } from "react-icons/bi";
import { POST, GET } from "../../service/CRUDService";
import {
  SIGN_IN_FIELD_LIMITS,
  state,
  schema,
} from "./SignInSideCurtains.utils";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import EntityConstants from "../../configs/EntityConstants";
import { isOtelAlertTelemetryEnabled } from "../../configs/featureFlags";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { GLogin, microsoftAuth } from "../../service/OAuthLogin";
import GoogleIcon from "../../resources/icons/Google-Social-icon.svg";
import MicrosoftIcon from "../../resources/icons/microsoft.svg";
import ForgotPasswordModal from "../../common/ForgotPasswordModal/ForgotPasswordModal";
import ReCAPTCHA from "react-google-recaptcha";
import AppConstants from "../../configs/AppConstants";
import useInterceptedNavigate from "../../hooks/useInterceptedNavigate";
import ErrorSignUpInNotification from "../../common/ErrorSignUpInNotification/ErrorSignUpInNotification";
import SignUpInModalLoader from "../../common/SignUpInModalLoader/SignUpInModalLoader";
import { emailRegex } from "../../configs/App.Regex";

const isValidEmailFormat = (email) =>
  emailRegex.test(email) && email.length <= SIGN_IN_FIELD_LIMITS.email;

const isNetworkFailure = (error) =>
  error?.otelNetworkLogged === true ||
  error?.name === "AbortError" ||
  (error?.name === "TypeError" &&
    /failed to fetch|load failed|networkerror|network request failed/i.test(
      String(error?.message || ""),
    ));

const reportUnexpectedLoginError = (errorMessage, error) => {
  if (!isOtelAlertTelemetryEnabled()) {
    return;
  }
  if (error?.response || error?.httpStatus != null || isNetworkFailure(error)) {
    return;
  }

  import("../../telemetry")
    .then((mod) => {
      if (typeof mod.logError === "function") {
        mod.logError(errorMessage, { event: "login_unexpected_error" });
      }
    })
    .catch(() => {});
};

const SignInSideCurtains = ({ toggleComponent, closeModal }) => {
  const { t } = useTranslation();
  const recaptchaRef = useRef(null);

  const navigate = useInterceptedNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const [hideShowpassword, setHideShowpassword] = useState("password");
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [signInError, setSignInError] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authProviders, setAuthProviders] = useState({
    signin: [],
    signup: [],
  });
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(true);
  const [emailForCaptchaCheck, setEmailForCaptchaCheck] = useState("");
  const [isLoadingOrgDetails, setIsLoadingOrgDetails] = useState(true);

  const InputBorderRadius = "0.5rem";
  const CheckboxFocusRadius = "0.3rem";

  const toggleForgotPasswordModal = () => {
    setIsForgotPasswordOpen((prev) => !prev);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleCloseErrorModal = () => {
    setSignInError("");
  };

  const fetchAuthenticatedUserWithPermissions = useCallback(async () => {
    const [userResult, permsResult] = await Promise.allSettled([
      GET(EntityConstants.CURRENT_USER),
      GET(EntityConstants.VERIFIER_PERMISSIONS),
    ]);

    if (userResult.status !== "fulfilled") {
      throw userResult.reason || new Error("Failed to fetch current user");
    }

    const userData = await userResult.value.json();

    let sidebar_components = [];
    if (permsResult.status === "fulfilled") {
      try {
        const permsData = await permsResult.value.json();
        sidebar_components = Array.isArray(permsData.sidebar_components)
          ? permsData.sidebar_components
          : [];
      } catch (permissionsError) {
        console.error("Failed to parse sidebar permissions:", permissionsError);
      }
    } else {
      console.error("Failed to fetch sidebar permissions:", permsResult.reason);
    }

    return {
      ...userData,
      sidebar_components,
    };
  }, []);

  const { login: authLogin, googlelogin: authGoogleLogin } = authContext;

  const completeLogin = useCallback(
    async ({ googleLogin = false } = {}) => {
      const userData = await fetchAuthenticatedUserWithPermissions();
      authLogin(userData);
      if (googleLogin) {
        authGoogleLogin();
      }
      localStorage.setItem("userType", userData.type?.toLowerCase());

      const redirectUrl = localStorage.getItem("redirectAfterLogin");
      if (redirectUrl) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectUrl);
      } else if (googleLogin || location.pathname === "/") {
        navigate("/verifier/validate");
      }

      closeModal();
    },
    [
      authGoogleLogin,
      authLogin,
      closeModal,
      fetchAuthenticatedUserWithPermissions,
      location.pathname,
      navigate,
    ],
  );

  const handleSubmit = async (values) => {
    try {
      setIsSigningIn(true);

      if (isCaptchaRequired && !captchaValue) {
        toast.error(t("homeAuthPages:captcha_complete"));
        return;
      }

      if (rememberMe) {
        const timestamp = new Date().getTime();
        localStorage.setItem("remembered_email", values.email);
        localStorage.setItem("remembered_password", values.password);
        localStorage.setItem("remembered_timestamp", timestamp.toString());
      } else {
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_password");
        localStorage.removeItem("remembered_timestamp");
      }

      const requestBody = isCaptchaRequired
        ? { ...values, captchaToken: captchaValue }
        : values;

      if (isCaptchaRequired) {
        recaptchaRef.current?.reset();
        setCaptchaValue(null);
      }

      const response = await POST(EntityConstants.LOGIN, requestBody);
      const data = await response.json();

      if (response.ok) {
        toast.success(t("homeAuthPages:logged_in_successfully"));
        localStorage.setItem("auth_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
        await completeLogin();
      } else {
        toast.error(data.error || t("homeAuthPages:error_occurred"));
      }
      setSignInError("");
    } catch (error) {
      if (error?.response) {
        try {
          const errorData = await error.response.json();
          if (errorData.error && errorData.message) {
            setSignInError(errorData);
          } else {
            errorData.error &&
              !errorData.message &&
              toast.error(errorData.error);
            errorData.message &&
              !errorData.error &&
              toast.error(errorData.message);
          }
        } catch {
          toast.error(t("homeAuthPages:unexpected_error"));
        }
      } else {
        if (isCaptchaRequired) {
          recaptchaRef.current?.reset();
          setCaptchaValue(null);
        }
        console.error("Login failed:", error);
        reportUnexpectedLoginError(
          error?.message || t("homeAuthPages:unexpected_error"),
          error,
        );
        toast.error(t("homeAuthPages:unexpected_error"));
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("remembered_email");
    const storedPassword = localStorage.getItem("remembered_password");
    const storedTimestamp = localStorage.getItem("remembered_timestamp");

    if (storedEmail && storedPassword && storedTimestamp) {
      const currentTime = new Date().getTime();
      const timeDifference = currentTime - parseInt(storedTimestamp, 10);

      if (timeDifference <= 30 * 24 * 60 * 60 * 1000) {
        state.email = storedEmail;
        state.password = storedPassword;
        setRememberMe(true);
        setEmailForCaptchaCheck(storedEmail.trim());
      } else {
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_password");
        localStorage.removeItem("remembered_timestamp");
      }
    } else if (storedEmail && storedPassword) {
      state.email = storedEmail;
      state.password = storedPassword;
      setRememberMe(true);
      setEmailForCaptchaCheck(storedEmail.trim());
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingOrgDetails(true);
        const response = await GET(EntityConstants.GET_ORGANISATION_DETAILS);
        const data = await response.json();

        if (response.ok) {
          setAuthProviders(data.auth_providers || []);
        } else {
          toast.error(t("homeAuthPages:failed_fetch_auth_providers"));
        }
      } catch (error) {
        toast.error(t("homeAuthPages:something_went_wrong"));
      } finally {
        setIsLoadingOrgDetails(false);
      }
    };

    fetchData();
  }, [t]);

  useEffect(() => {
    const email = emailForCaptchaCheck.trim();

    if (!email || !isValidEmailFormat(email)) {
      setIsCaptchaRequired(true);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await GET(
          `${EntityConstants.VERIFIER_PERMISSIONS}?email=${encodeURIComponent(
            email,
          )}`,
        );
        const data = await response.json();

        if (cancelled) return;

        const required = data.recaptcha_required !== false;
        setIsCaptchaRequired(required);
        if (!required) {
          setCaptchaValue(null);
          recaptchaRef.current?.reset();
        }
      } catch (error) {
        if (cancelled) return;
        setIsCaptchaRequired(true);
        console.error("Failed to check recaptcha permission:", error);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailForCaptchaCheck]);

  const login = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      const loginSuccess = await GLogin(credentialResponse, "success");
      if (loginSuccess === true) {
        try {
          await completeLogin({ googleLogin: true });
        } catch (error) {
          toast.error(t("homeAuthPages:failed_fetch_user_data"));
        }
      }
    },
    onError: (error) => {
      GLogin(error, "failed");
    },
  });

  if (isLoadingOrgDetails) {
    return (
      <div className="flex justify-center items-center h-64">
        <SignUpInModalLoader />
      </div>
    );
  }

  if (!signInError?.error) {
    return (
      <>
        <Formik
          enableReinitialize
          initialValues={state}
          validationSchema={schema}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({ values, touched, errors, handleChange, handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <div className="py-4">
                <h3 className="text-4xl font-medium leading-6 mt-1 text-cfSignInSignUpHeadingText px-8">
                  {t("homeAuthPages:sign_in")}
                </h3>
                <div className="flex flex-col justify-center items-center text-left w-full h-full overflow-hidden font-CfPrimaryFont px-8 py-6">
                  <div className="py-3">
                    <p className="text-cfSignInSignUpIntroMessageText text-base">
                      {t("homeAuthPages:sign_in_description")}
                    </p>
                  </div>
                  {authProviders.signin.includes("credentials") && (
                    <div
                      id="inputSignUp"
                      className={`flex flex-col gap-[0.625rem] w-full`}
                    >
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full`}
                      >
                        <label
                          htmlFor="sign-in-email-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-cfSignInSignUpRequiredAsterisk"
                        >
                          {t("homeAuthPages:email")}&nbsp;
                        </label>

                        <input
                          type="text"
                          name="email"
                          id="sign-in-email-input"
                          required
                          placeholder={t("homeAuthPages:enter_email")}
                          autoComplete="true"
                          maxLength={SIGN_IN_FIELD_LIMITS.email}
                          className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                          style={{ borderRadius: InputBorderRadius }}
                          value={values.email.trim()}
                          onChange={(e) => {
                            handleChange(e);
                            setEmailForCaptchaCheck(e.target.value.trim());
                          }}
                        />
                      </div>
                      <p
                        id="sign-in-email-error-description"
                        className="text-cfSignInSignUpErrorText"
                      >
                        {errors.email && touched.email && `${errors.email}`}
                      </p>
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full`}
                      >
                        <label
                          htmlFor="sign-in-password-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-cfSignInSignUpRequiredAsterisk"
                        >
                          {t("homeAuthPages:password")}&nbsp;
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 pl-2 border border-cfSignInSignUpInputBorder">
                          <input
                            type={hideShowpassword}
                            name="password"
                            id="sign-in-password-input"
                            required
                            placeholder={t("homeAuthPages:password")}
                            autoComplete="true"
                            maxLength={SIGN_IN_FIELD_LIMITS.password}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                            value={values.password.trim()}
                            style={{ borderRadius: InputBorderRadius }}
                            onChange={handleChange}
                          />
                          <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                            {hideShowpassword === "password" && (
                              <BiShow
                                id="sign-in-show-password-button"
                                onClick={() => setHideShowpassword("text")}
                                className="cursor-pointer"
                              />
                            )}
                            {hideShowpassword === "text" && (
                              <BiHide
                                id="sign-in-hide-password-button"
                                onClick={() => setHideShowpassword("password")}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <p
                        id="sign-in-password-error-description"
                        className="text-cfSignInSignUpErrorText"
                      >
                        {errors.password &&
                          touched.password &&
                          `${errors.password}`}
                      </p>

                      <div className="flex text-base justify-between items-center w-full mb-2">
                        <div>
                          <input
                            type="checkbox"
                            name="remember"
                            id="remember"
                            className="focus:ring-transparent focus:outline-none peer mx-1 mb-0.5 border border-cfSignInSignUpInputBorder"
                            style={{ borderRadius: CheckboxFocusRadius }}
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                          />
                          <label
                            htmlFor="remember"
                            className={`mb-0 ml-1 text-sm font-medium`}
                          >
                            {t("homeAuthPages:remember_30_days")}
                          </label>
                        </div>
                        <div>
                          <a
                            id="sign-in-forgot-password-link"
                            href="#forgot_password"
                            className={`text-cfForgotPasswordText hover:text-cfPrimaryDark text-sm font-medium focus:outline-none`}
                            onClick={(e) => {
                              e.preventDefault();
                              toggleForgotPasswordModal();
                            }}
                          >
                            {t("homeAuthPages:forgot_password")}
                          </a>
                        </div>
                      </div>

                      <div
                        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${
                          isCaptchaRequired
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          {isCaptchaRequired && (
                            <div className="flex custom-recaptcha-width justify-center items-center">
                              <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                                onChange={handleCaptchaChange}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-center">
                        <input
                          id="sign-in-submit-button"
                          type="submit"
                          value={t("homeAuthPages:sign_in")}
                          disabled={
                            !values.email?.trim() ||
                            !values.password?.trim() ||
                            (isCaptchaRequired && !captchaValue)
                          }
                          className="rounded-xl text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark py-1 px-5 text-cfSecondary w-full h-11 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-cfButton"
                        />
                      </div>
                    </div>
                  )}

                  {authProviders.signin.filter((p) =>
                    ["google", "microsoft"].includes(p),
                  ).length > 0 &&
                    authProviders.signin.includes("credentials") && (
                      <div className="flex items-center w-full gap-2.5 text-cfSignInSignUpOrText">
                        <hr className="w-1/2" />
                        <p>{t("homeAuthPages:or")}</p>
                        <hr className="w-1/2" />
                      </div>
                    )}

                  <div className="flex mt-[0.5rem] gap-4 w-full">
                    {authProviders.signin.includes("google") && (
                      <button
                        id="sign-in-google-oauth-button"
                        type="button"
                        onClick={login}
                        className="flex items-center gap-2 justify-center w-full h-11 border rounded-full"
                      >
                        <img src={GoogleIcon} alt="google" />
                        <p className="font-semibold text-base font-CfPrimaryFont">
                          {t("homeAuthPages:sign_in")}
                        </p>
                      </button>
                    )}
                    {authProviders.signin.includes("microsoft") && (
                      <button
                        id="sign-in-microsoft-oauth-button"
                        type="button"
                        onClick={microsoftAuth}
                        className="flex items-center gap-2 justify-center w-full h-11 border rounded-full"
                      >
                        <img src={MicrosoftIcon} alt="microsoft" />
                        <p className="font-semibold text-base font-CfPrimaryFont">
                          {t("homeAuthPages:sign_in")}
                        </p>
                      </button>
                    )}
                  </div>
                  {authProviders.signin.includes("credentials") && (
                    <div className="flex text-[1rem] leading-[1.2rem] mt-3 mb-3">
                      <p className="text-cfDontHaveAccountText">
                        {t("homeAuthPages:no_account")}
                      </p>
                      &nbsp;
                      <button
                        id="sign-in-sign-up-toggle-button"
                        onClick={toggleComponent}
                        className="flex focus:outline-none"
                      >
                        <p className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                          {t("homeAuthPages:sign_up")}
                        </p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {isForgotPasswordOpen && (
          <ForgotPasswordModal
            isModalOpen={isForgotPasswordOpen}
            toggleModal={setIsForgotPasswordOpen}
            certificate_header={t("homeAuthPages:reset_password")}
            certificate_description={t(
              "homeAuthPages:reset_password_description",
            )}
          />
        )}
        {isSigningIn && (
          <div className="rounded-[1.25rem]">
            <SignUpInModalLoader />
          </div>
        )}
      </>
    );
  } else if (signInError?.error) {
    return (
      <ErrorSignUpInNotification
        closeModal={handleCloseErrorModal}
        error={signInError?.error}
        message={signInError?.message}
        t={t}
      />
    );
  }
};

export default SignInSideCurtains;
